import { ActionType, ProTable } from "@ant-design/pro-components";
import { useGlobalState } from "../../components/global";
import { useEffect, useRef } from "react";
import UUIDComponent from "../../components/UUIDComponent";
import { Button, Form, message, Modal, Switch } from "antd";
import GlobalWrap from "../../components/globalWrap";
import VIPPackageForm from "./VIPPackageForm";
import { deviceType, labelType, payType, vipType } from "../const";
import { PlusOutlined } from "@ant-design/icons";

function formatVersionRange(record: any) {
    const { minVersionCode, maxVersionCode } = record;
    if (!minVersionCode && !maxVersionCode) return "全部版本";
    if (!minVersionCode) return `不高于 ${maxVersionCode}`;
    if (!maxVersionCode) return `不低于 ${minVersionCode}`;
    return `${minVersionCode} - ${maxVersionCode}`;
}

export default function VIPPackage() {
    const { jsonRequest } = useGlobalState();
    const tableRef = useRef<ActionType>();
    const [form] = Form.useForm();

    useEffect(() => {
        tableRef.current?.reload();
    }, []);

    const columns: any[] = [
        {
            title: "序号",
            valueType: "index",
            search: false,
            align: "center",
        },
        {
            title: "ID",
            dataIndex: "id",
            align: "center",
            search: false,
            render: (value: any) => <UUIDComponent value={value} />
        },
        {
            title: "会员卡标题",
            dataIndex: "title",
            align: "center",
            search: false,
        },
        {
            title: "优惠价格",
            dataIndex: "discountPrice",
            align: "center",
            search: false,
            render: (text: number) => (text / 100).toFixed(2) + '元',
        },
        {
            title: "真实价格",
            dataIndex: "realPrice",
            align: "center",
            search: false,
            render: (text: number) => (text / 100).toFixed(2) + '元',
        },
        {
            title: "划线价",
            dataIndex: "strikethroughPrice",
            align: "center",
            search: false,
            render: (text: number) => (text / 100).toFixed(2) + '元',
        },
        {
            title: "标签文案",
            dataIndex: "labelType",
            align: "center",
            search: false,
            valueEnum: labelType,
        },
        {
            title: "会员卡底部文案",
            dataIndex: "bottomText",
            align: "center",
            search: false,
        },
        {
            title: "苹果商品编码",
            dataIndex: "appleCode",
            align: "center",
            search: false,
        },
        {
            title: "会员卡类型",
            dataIndex: "vipType",
            align: "center",
            search: false,
            valueEnum: vipType,
        },
        {
            title: "适用 App build",
            dataIndex: "minVersionCode",
            align: "center",
            search: false,
            render: (_: any, record: any) => formatVersionRange(record),
        },
        {
            title: "适用平台",
            dataIndex: "deviceType",
            align: "center",
            search: true,
            valueEnum: deviceType,
        },
        {
            title: "支付方式",
            dataIndex: "payType",
            align: "center",
            search: false,
            valueEnum: payType,
        },
        {
            title: "排序",
            dataIndex: "sort",
            align: "center",
            search: false,
        },
        {
            title: "状态",
            dataIndex: "onLineFlag",
            align: "center",
            search: false,
            render: (text: boolean, record: any) =>
                <Switch
                    defaultChecked
                    checked={text}
                    checkedChildren="上架"
                    unCheckedChildren="下架"
                    onChange={() => updateStatus(record)}
                />
        },
        {
            title: "创建时间",
            dataIndex: "createAt",
            align: "center",
            search: false,
        },
        {
            title: "操作",
            key: 'operation',
            search: false,
            align: "center",
            render: (_: any, record: any) => <>
                <Button type="link" onClick={() => handleAddOrEdit(record)}>编辑</Button>
            </>,
        }
    ];

    const handleAddOrEdit = (item: any) => {
        Modal.confirm({
            width: 600,
            title: !!item.id ? `会员套餐编辑 - ${item.title}` : "会员套餐新增",
            content: <GlobalWrap><VIPPackageForm form={form} item={item} /></GlobalWrap>,
            onOk: async (close) => {
                let values = await form.validateFields();
                if (!!item?.id) {
                    await jsonRequest.put("/back/vip-package/modify", {
                        ...values,
                        discountPrice: values.discountPrice * 100,
                        realPrice: values.realPrice * 100,
                        strikethroughPrice: values.strikethroughPrice * 100,
                        id: item.id,
                    }).then((resp: any) => resp.json())
                        .then((data: any) => {
                            if (data?.code === 0) {
                                message.success("编辑成功");
                            } else {
                                message.error(data?.msg || "编辑失败");
                            }
                        }
                        )
                } else {
                    await jsonRequest.post("/back/vip-package/add", {
                        ...values,
                        discountPrice: values.discountPrice * 100,
                        realPrice: values.realPrice * 100,
                        strikethroughPrice: values.strikethroughPrice * 100,
                    }).then((resp: any) => resp.json())
                        .then((data: any) => {
                            if (data?.code === 0) {
                                message.success("新增成功");
                            } else {
                                message.error(data?.msg || "新增失败");
                            }
                        }
                        )
                }
                tableRef.current?.reload();
                form.resetFields();
                close();
                return false;
            },
            onCancel: (close) => {
                form.resetFields();
                close();
            }
        })
    }

    const updateStatus = async (record: any) => {
        await jsonRequest.put(`/back/vip-package/change-on-line`, {
            id: record.id,
        },
        ).then((resp: any) => resp.json())
            .then((data: any) => {
                if (data?.code === 0) {
                    message.success(`${record.onLineFlag ? "下架" : "上架"}成功`);
                } else {
                    message.error(data?.msg || "编辑失败");
                }
                tableRef.current?.reload();
            })
    }

    const request = async (params: any) => {
        const result: any = await jsonRequest.get("/back/vip-package/page", {
            currentPage: params.current,
            pageSize: params.pageSize,
            deviceType: params?.deviceType,
        }).then((resp: any) => resp.json());
        const { data } = result;
        return { data: data?.data, success: result.code === 0, total: data?.count };
    };

    return <>
        <h1>VIP套餐列表</h1>
        <ProTable
            rowKey="id"
            scroll={{ x: 100 }}
            actionRef={tableRef}
            cardBordered
            columns={columns}
            request={request}
            pagination={{ defaultPageSize: 10, showSizeChanger: true }}
            search={{ span: 6, defaultCollapsed: false }}
            toolBarRender={() => [<Button
                key="button"
                icon={<PlusOutlined />}
                onClick={() => handleAddOrEdit({})}
                type="primary"
            >
                新增
            </Button>]}
        />
    </>;
}
